import type { CSSProperties, PropsWithChildren } from 'react';

import { css, keyframes } from '@sorare/css';

import { ResponsiveImg } from '#src/atoms/ui/ResponsiveImg/index.tsx';
import type { Unit } from '#src/lib/style.ts';

const SETTLE_MS = 400;
const EXPAND_MS = 350;
const CONTENT_MS = 150;
const HOLD_MS = 5000;
const COLLAPSE_MS = 250;
const COLLAPSE_DELAY_MS = SETTLE_MS + EXPAND_MS + CONTENT_MS + HOLD_MS;

const unfurlFrames = keyframes`
  from {
    clip-path: inset(0 var(--callout-hidden-inset));
  }
  to {
    clip-path: inset(0 0 0 0);
  }
`;

const retractFrames = keyframes`
  from {
    clip-path: inset(0 0 0 0);
  }
  to {
    clip-path: inset(0 var(--callout-hidden-inset));
  }
`;

const fadeOutFrames = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const root = css`
  position: relative;
  display: flex;
`;

const callout = css`
  position: absolute;
  top: 0;
  height: var(--badge-size);
  display: flex;
  align-items: center;
  pointer-events: none;
  animation: ${retractFrames} ${COLLAPSE_MS}ms var(--ease-in-quad)
    ${COLLAPSE_DELAY_MS}ms both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const unfurlToTheRight = css`
  --callout-hidden-inset: 100% 0 0;
  left: calc(var(--badge-size) / 2);
`;

const unfurlToTheLeft = css`
  --callout-hidden-inset: 0 0 100%;
  right: calc(var(--badge-size) / 2);
`;

const banner = css`
  animation:
    ${unfurlFrames} ${EXPAND_MS}ms var(--ease-out-quad) ${SETTLE_MS}ms both,
    ${fadeOutFrames} ${CONTENT_MS}ms var(--ease-in-quad) ${COLLAPSE_DELAY_MS}ms
      both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const artwork = css`
  display: block;
  height: var(--badge-size);
  width: auto;
`;

type Props = PropsWithChildren<{
  artworkUrl: Nullable<string>;
  size: Unit;
  unfurlTowards?: 'left' | 'right';
}>;

export const WidgetCallout = ({
  artworkUrl,
  size,
  unfurlTowards = 'right',
  children,
}: Props) => (
  <div
    className={root}
    style={{ '--badge-size': `calc(${size} * var(--unit))` } as CSSProperties}
  >
    {!!artworkUrl && (
      <div
        className={`${callout} ${unfurlTowards === 'left' ? unfurlToTheLeft : unfurlToTheRight}`}
        aria-hidden
      >
        <div className={banner}>
          <ResponsiveImg
            src={artworkUrl}
            alt=""
            cropWidth={320}
            className={artwork}
          />
        </div>
      </div>
    )}
    {children}
  </div>
);
